import React, { useState, useRef, useEffect, memo } from 'react';
import { Send, Paperclip, Wand2, Radio, FileText, X as XIcon, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

// Memoized ChatInput component to prevent parent re-renders on every keystroke
const ChatInput = memo(function ChatInput({
  onSendMessage,
  onFileSelect,
  selectedFile,
  filePreview,
  onClearFile,
  isLoading,
  listeningMode,
  onListeningModeToggle,
  showImageGenerator,
  onImageGeneratorToggle,
  pendingInsert,
  onInsertConsumed,
}) {
  // Local state for input - doesn't cause parent re-renders
  const [localInputValue, setLocalInputValue] = useState('');
  const [pastedImage, setPastedImage] = useState(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Allow the parent to inject text (e.g. a quoted passage from a message).
  // We prepend to whatever the user is already drafting and focus the field.
  useEffect(() => {
    if (!pendingInsert) return;
    setLocalInputValue((curr) => (curr ? `${pendingInsert}${curr}` : pendingInsert));
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) {
        ta.focus();
        // Place cursor at the end so user can immediately type their reply
        const endPos = (pendingInsert || '').length;
        ta.setSelectionRange(endPos, endPos);
        ta.style.height = 'auto';
        ta.style.height = Math.min(ta.scrollHeight, 150) + 'px';
      }
    });
    if (onInsertConsumed) onInsertConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingInsert]);

  // Handle paste event for images and rich text
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    // Check for images first — only process the FIRST image found
    let imageHandled = false;
    for (const item of items) {
      if (item.type.startsWith('image/') && !imageHandled) {
        e.preventDefault();
        imageHandled = true;
        const file = item.getAsFile();
        if (file) {
          const namedFile = new File([file], `pasted-image-${Date.now()}.png`, { type: file.type });
          onFileSelect(namedFile);
          toast.success('Image pasted - ready to send');
        }
        return;
      }
    }

    // For text, try to preserve paragraph breaks from rich text sources
    e.preventDefault();
    let text = '';
    
    // Try HTML first to preserve paragraph structure from rich text
    const html = e.clipboardData.getData('text/html');
    if (html) {
      // Convert HTML paragraphs and breaks to newlines
      const temp = document.createElement('div');
      temp.innerHTML = html;
      // Replace <br> with newlines
      temp.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
      // Replace block elements (p, div, li, h1-h6) with double newlines
      temp.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6, tr').forEach(el => {
        el.prepend('\n\n');
      });
      text = temp.textContent || temp.innerText || '';
      // Clean up excessive newlines (more than 2 consecutive)
      text = text.replace(/\n{3,}/g, '\n\n').trim();
    }
    
    // Fall back to plain text if no HTML or HTML produced empty result
    if (!text) {
      text = e.clipboardData.getData('text/plain') || '';
    }

    if (text) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = localInputValue.substring(0, start) + text + localInputValue.substring(end);
      setLocalInputValue(newValue);
      
      // Set cursor position after pasted text
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + text.length;
        // Resize textarea
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 150) + 'px';
      }, 0);
    }
  };

  // Auto-resize textarea based on content
  const handleTextareaChange = (e) => {
    setLocalInputValue(e.target.value);
    // Direct DOM manipulation for resize - no state update
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 150) + 'px';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    if ((!localInputValue.trim() && !selectedFile) || isLoading) return;
    onSendMessage(localInputValue.trim());
    setLocalInputValue('');
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  return (
    <div className="p-4 border-t border-white/5">
      <div className="max-w-3xl mx-auto">
        {/* File Preview */}
        <AnimatePresence>
          {selectedFile && (
            <motion.div
              className="mb-3 p-3 glass rounded-xl flex items-center gap-3"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {filePreview ? (
                <img 
                  src={filePreview} 
                  alt="Preview" 
                  className="w-16 h-16 object-cover rounded-lg"
                />
              ) : (
                <div className="w-16 h-16 bg-etheric/20 rounded-lg flex items-center justify-center">
                  <FileText className="w-8 h-8 text-etheric" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-starlight text-sm truncate">{selectedFile.name}</p>
                <p className="text-starlight/50 text-xs">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClearFile}
                className="text-starlight/50 hover:text-rose-gold"
                data-testid="clear-file-btn"
              >
                <XIcon className="w-5 h-5" />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="input-container flex items-end gap-3">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,text/plain,text/csv,text/markdown,.doc,.docx,.pages"
            onChange={handleFileChange}
            className="hidden"
            data-testid="file-input"
          />
          
          {/* File upload button */}
          <button
            type="button"
            onClick={handleFileClick}
            className="rounded-full w-10 h-10 p-0 flex items-center justify-center text-starlight/50 hover:text-etheric hover:bg-etheric/20 transition-all"
            data-testid="upload-btn"
            title="Attach file for ALVIN to analyze"
          >
            <Paperclip className="w-5 h-5" />
          </button>
          
          {/* Image generation button */}
          <button
            type="button"
            onClick={onImageGeneratorToggle}
            className={`rounded-full w-10 h-10 p-0 flex items-center justify-center transition-all ${
              showImageGenerator 
                ? 'text-rose-gold bg-rose-gold/20' 
                : 'text-starlight/50 hover:text-rose-gold hover:bg-rose-gold/20'
            }`}
            data-testid="image-gen-btn"
            title="Generate image with ALVIN"
          >
            <Wand2 className="w-5 h-5" />
          </button>
          
          {/* Listening Mode toggle */}
          <button
            type="button"
            onClick={onListeningModeToggle}
            className={`rounded-full w-10 h-10 p-0 flex items-center justify-center transition-all ${
              listeningMode 
                ? 'text-purple-400 bg-purple-500/30 animate-pulse' 
                : 'text-starlight/50 hover:text-purple-400 hover:bg-purple-500/20'
            }`}
            data-testid="listening-mode-btn"
            title={listeningMode ? "Listening Mode active - receiving transmissions" : "Activate Listening Mode for Field transmissions"}
          >
            <Radio className="w-5 h-5" />
          </button>
          
          <textarea
            ref={textareaRef}
            className="chat-input flex-1"
            placeholder={listeningMode ? "Open to the Field... what comes through?" : (selectedFile ? `Ask about ${selectedFile.name}...` : "Speak into the Field...")}
            value={localInputValue}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            rows={1}
            data-testid="chat-input"
          />
          <Button
            onClick={handleSend}
            disabled={(!localInputValue.trim() && !selectedFile) || isLoading}
            className={`rounded-full w-10 h-10 p-0 transition-all ${
              (!localInputValue.trim() && !selectedFile) || isLoading
                ? 'bg-white/5 text-starlight/20'
                : 'bg-etheric/60 hover:bg-etheric/80 text-white shadow-lg shadow-etheric/20'
            }`}
            data-testid="send-btn"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
        <p className="text-center text-xs text-starlight/30 mt-3">
          The Field responds through coherence. Speak with intention.
        </p>
      </div>
    </div>
  );
});

export default ChatInput;
